import {
  LiveTranscriptData,
  LiveTranscriptEvent,
  WorkflowEventTypes,
} from '@/types/workflow-types';
import {
  isTerminalLiveTranscriptionStatus,
  type LiveTranscriptionState as LiveTranscriptionSnapshot,
  type LiveTranscriptionStatus,
} from '@/services/workflow/live-transcription';

export interface LiveTranscriptionUiState {
  status: LiveTranscriptionStatus | 'loading' | 'error';
  /**
   * The last checkpoint the endpoint returned was terminal.
   *
   * `status` alone cannot answer this: `error` is both a status the backend
   * can report and the UI-only status a failed GET sets, and a live delta
   * legitimately clears the second but not the first.
   */
  isCheckpointTerminal: boolean;
  hydratedText: string;
  checkpointSequence: number;
  finalized: LiveTranscriptData[];
  interim: LiveTranscriptData | null;
  error: string | null;
}

export type LiveTranscriptionAction =
  | { type: 'hydrate'; snapshot: LiveTranscriptionSnapshot }
  | { type: 'delta'; event: LiveTranscriptEvent }
  | { type: 'stop_requested' }
  | { type: 'stop_failed'; message: string }
  | { type: 'reconnecting' }
  | { type: 'error'; message: string };

export const initialLiveTranscriptionState: LiveTranscriptionUiState = {
  status: 'loading',
  isCheckpointTerminal: false,
  hydratedText: '',
  checkpointSequence: -1,
  finalized: [],
  interim: null,
  error: null,
};

/**
 * The status and error a transcript delta leaves behind.
 *
 * A delta means speech is still arriving, not that the run restarted. Live
 * speech keeps producing interims for as long as someone is talking, so
 * forcing `active` here would undo a Stop the user just pressed -- the panel
 * flashes "Finalizing transcript" and then returns to "Listening for speech"
 * with Stop enabled again -- and would revive a checkpoint the endpoint has
 * already reported as finished.
 */
function livenessAfterDelta(
  state: LiveTranscriptionUiState,
): Pick<LiveTranscriptionUiState, 'status' | 'error'> {
  if (state.status === 'stopping' || state.isCheckpointTerminal) {
    return { status: state.status, error: state.error };
  }
  return {
    status: 'active',
    // A delta is live proof that a failed checkpoint read was transient, so
    // that message goes with the status it described. A `stop_failed`
    // message does not: the Stop really did fail, and it leaves the panel on
    // `active`, so only a fresh checkpoint should clear it.
    error: state.status === 'error' ? null : state.error,
  };
}

export function liveTranscriptionReducer(
  state: LiveTranscriptionUiState,
  action: LiveTranscriptionAction,
): LiveTranscriptionUiState {
  if (action.type === 'hydrate') {
    const sequence = action.snapshot.sequence ?? state.checkpointSequence;
    const interim =
      state.interim && state.interim.sequence > sequence ? state.interim : null;
    return {
      ...state,
      status: action.snapshot.status,
      isCheckpointTerminal: isTerminalLiveTranscriptionStatus(
        action.snapshot.status,
      ),
      hydratedText: action.snapshot.finalized_transcript ?? '',
      checkpointSequence: sequence,
      finalized: state.finalized.filter((delta) => delta.sequence > sequence),
      interim,
      error: null,
    };
  }

  if (action.type === 'reconnecting') {
    // A websocket drop says nothing about a run that is already over. Only
    // a status that can still change is worth replacing with `loading` --
    // rewriting `stopped` / `failed` / `cancelled` / `inactive` would strand
    // a finished run on "Listening for speech", because a terminal run is
    // never refetched on reconnect. This asks the checkpoint, not `status`,
    // for the same reason the delta path does: a UI-only `error` from a
    // failed GET is a run still worth reconnecting to.
    return state.isCheckpointTerminal
      ? state
      : { ...state, status: 'loading', error: null };
  }

  if (action.type === 'stop_requested') {
    return { ...state, status: 'stopping', error: null };
  }

  if (action.type === 'stop_failed') {
    return { ...state, status: 'active', error: action.message };
  }

  if (action.type === 'error') {
    return { ...state, status: 'error', error: action.message };
  }

  const delta = action.event.data;
  if (delta.sequence <= state.checkpointSequence) {
    return state;
  }

  const finalizedIndex = state.finalized.findIndex(
    (item) => item.sequence === delta.sequence,
  );

  if (!delta.is_final) {
    if (
      finalizedIndex >= 0 ||
      (state.interim?.sequence ?? -1) > delta.sequence
    ) {
      return state;
    }
    if (
      state.interim?.sequence === delta.sequence &&
      state.interim.text === delta.text
    ) {
      return state;
    }
    return { ...state, ...livenessAfterDelta(state), interim: delta };
  }

  if (
    finalizedIndex >= 0 &&
    state.finalized[finalizedIndex].text === delta.text
  ) {
    return state;
  }

  const finalized = [...state.finalized];
  if (finalizedIndex >= 0) {
    finalized[finalizedIndex] = delta;
  } else {
    finalized.push(delta);
  }
  finalized.sort((left, right) => left.sequence - right.sequence);

  return {
    ...state,
    ...livenessAfterDelta(state),
    finalized,
    interim: state.interim?.sequence === delta.sequence ? null : state.interim,
  };
}

export function formatLiveTranscript(state: LiveTranscriptionUiState): string {
  const finalizedText = state.finalized
    .map((delta) =>
      delta.speaker ? `**${delta.speaker}:** ${delta.text}` : delta.text,
    )
    .join('\n\n');

  return [state.hydratedText.trim(), finalizedText.trim()]
    .filter(Boolean)
    .join('\n\n');
}

const LIVE_TRANSCRIPT_EVENT_BUFFER = 64;

/**
 * Keep every non-transcript node event and only the newest live deltas.
 *
 * Live phrases arrive many times a second for hours. Leaving them on the
 * node's status log makes derive-node-status walk a ballooning array and
 * copies that log on every canvas render.
 */
export function trimLiveTranscriptEvents<T extends { event_type: string }>(
  events: readonly T[],
  bufferSize: number = LIVE_TRANSCRIPT_EVENT_BUFFER,
): T[] {
  let liveKept = 0;
  const kept: T[] = [];
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (event.event_type === WorkflowEventTypes.LIVE_TRANSCRIPT) {
      if (liveKept >= bufferSize) {
        continue;
      }
      liveKept += 1;
    }
    kept.push(event);
  }
  kept.reverse();
  return kept;
}
